import { AsyncPipe, NgClass } from '@angular/common';
import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewEncapsulation, ViewRef } from '@angular/core';
import { Product, RoutingService, TranslatePipe } from '@spartacus/core';
import { BreakpointService, CmsComponentData, MediaComponent } from '@spartacus/storefront';
import { DeviceDetectorService } from 'ngx-device-detector';
import { SlickCarouselModule } from 'ngx-slick-carousel';
import { Observable, Subscription } from 'rxjs';
import { filter, map, pairwise, startWith, switchMap, take, tap } from 'rxjs/operators';
import { CheckoutConstants } from 'src/app/model/checkout.model';
import { DealerPrice } from 'src/app/model/dealer-price.model';
import { ShimanoProduct } from 'src/app/model/product.model';
import { CheckoutServiceExtends } from 'src/app/service/checkout/checkout.service';
import { ComponentService } from 'src/app/service/component.service';
import { ShimanoCountryService } from 'src/app/service/country/shimano-country.service';
import { DealerPriceService } from 'src/app/service/dealer-price/dealer-price.service';
import {
  GoogleTagManagerEvent,
  GoogleTagManagerService,
} from 'src/app/service/googletagmanager/google-tag-manager.service';
import { ShimanoLanguageService } from 'src/app/service/language/shimano-language.service';
import { StorageService } from 'src/app/service/storage/storage.service';
import { CAROUSEL_NORMAL, TooltipIconService } from 'src/app/service/tooltip-icon/tooltip-icon.service';
import { CurrentBusinessType } from 'src/app/utility/current-businesstype';
import { ShimanoUtilMethod } from 'src/app/utility/methods.util';
import { RemoveImageTitleAttrDirective } from '../../../shared/remove-title-attr.directive';
import { SlickCarouselUtilService } from '../../../utility/slick-carousel.util';
import { ErpPromotionalTextComponent } from '../promotional-text/erp-promotional-text/erp-promotional-text.component';
import { PromotionalTextComponent } from '../promotional-text/promotional-text.component';
import { POINTER_EVENTS_NONE_CLASS, TooltipIconComponent } from '../tooltip-icon/tooltip-icon.component';
import { ShimanoCmsProductCarouselComponent } from './product-carousel.component';

@Component({
  selector: 'app-product-carousel-normal',
  templateUrl: './product-carousel-normal.component.html',
  styleUrls: ['./product-carousel.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    SlickCarouselModule,
    MediaComponent,
    RemoveImageTitleAttrDirective,
    NgClass,
    TooltipIconComponent,
    ErpPromotionalTextComponent,
    PromotionalTextComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class ProductCarouselNormalComponent implements OnInit, OnDestroy {
  private componentData$: Observable<ShimanoCmsProductCarouselComponent> = this.componentData.data$.pipe(
    filter(Boolean)
  );
  slickConfig: any;
  showSrp = false;
  srpSubscription$: Subscription;
  isOutlet$: Observable<boolean> = this.componentData$.pipe(map((data) => data.uid.toUpperCase().includes('OUTLET')));
  atLeastOneHasHybrisPromo = false;
  atLeastOneHasErpPromo = false;
  promoMaxWidth = 0;

  activeCountry$: Observable<string> = this.countryService.getActive();

  noPointerEventsOnElement: string;
  tooltipIconConfig$: Observable<any> = this.activeCountry$.pipe(
    take(1),
    switchMap((activeCountry) => this.tooltipIconService.getTooltipIconConfig(activeCountry, CAROUSEL_NORMAL)),
    tap((tooltipInfoConfig) => (this.noPointerEventsOnElement = tooltipInfoConfig.noPointerEventsOnElement))
  );

  showGstExclusive$: Observable<boolean> = this.activeCountry$.pipe(
    take(1),
    switchMap((activeCountry) =>
      this.checkoutServiceExtends.isCheckoutFeatureEnabled$(activeCountry, CheckoutConstants.GST_EXCLUSIVE)
    )
  );

  showGstInclusive$: Observable<boolean> = this.activeCountry$.pipe(
    take(1),
    switchMap((activeCountry) =>
      this.checkoutServiceExtends.isCheckoutFeatureEnabled$(activeCountry, CheckoutConstants.GST_INCLUSIVE)
    )
  );
  businessType: string;

  gtmbusinessType: string = this.currentBusinesstype.getBusinessType();
  affiliation = `/${this.gtmbusinessType}/${this.gtmActiveCountry}/${this.activeLanguage}`;

  constructor(
    private deviceService: DeviceDetectorService,
    protected componentService: ComponentService,
    protected dealerPriceService: DealerPriceService,
    protected cdr: ChangeDetectorRef,
    private gtmService: GoogleTagManagerService,
    private countryService: ShimanoCountryService,
    private tooltipIconService: TooltipIconService,
    private routingService: RoutingService,
    private checkoutServiceExtends: CheckoutServiceExtends,
    protected componentData: CmsComponentData<any>,
    private breakpointService: BreakpointService,
    private currentBusinesstype: CurrentBusinessType,
    private languageService: ShimanoLanguageService,
    private storageService: StorageService
  ) {
    const slickConfig = new SlickCarouselUtilService();
    const slickObj = slickConfig.defaultConfig;
    if (!this.deviceService.isDesktop()) {
      slickObj.arrows = false;
    }
    this.slickConfig = slickObj;
  }

  @Input() dealerPrices: DealerPrice[];
  @Input() location: string;
  @Input() items$: Observable<ShimanoProduct[]>;
  hasDealerPrice = true;
  dealerPriceCheckerSubscription$: Subscription;

  ngOnInit() {
    this.items$.pipe(take(1)).subscribe((products) => {
      if (products) {
        this.gtmProductCarouselNormalImpressions(products, products.length);
        products.forEach((product) => {
          if (
            !this.atLeastOneHasHybrisPromo &&
            product.potentialPromotions &&
            product.potentialPromotions[0].description
          )
            this.atLeastOneHasHybrisPromo = true;
          if (!this.atLeastOneHasErpPromo && this.erpPromoValue(product.code)) this.atLeastOneHasErpPromo = true;
        });
      }
    });

    this.getInitSrpToggleValue();

    this.srpSubscription$ = this.componentService.srpToggle$.subscribe((checked) => {
      this.showSrp = checked;
      this.cdr.detectChanges();
    });

    this.dealerPriceCheckerSubscription$ = this.componentService.hasDealerPrice$.subscribe((hasDealerPrice) => {
      this.hasDealerPrice = hasDealerPrice;
      if (!(this.cdr as ViewRef).destroyed) {
        this.cdr.detectChanges();
      }
    });
    this.businessType = this.currentBusinesstype.getBusinessType();
  }

  get gtmActiveCountry(): string {
    let activeCountry: string;
    this.countryService.getActive().subscribe((country) => (activeCountry = country));
    return activeCountry;
  }

  get activeLanguage(): string {
    let activeLanguage: string;
    this.languageService.getActive().subscribe((lang) => (activeLanguage = lang));
    return activeLanguage;
  }

  getInitSrpToggleValue() {
    this.showSrp = this.storageService.getLocalStorageItem('showSrp') === 'true';
  }

  dealerPrice(productCode: string): string {
    if (this.dealerPrices) {
      return this.dealerPriceService.getDealerPriceByProductCode(this.dealerPrices, productCode);
    }
  }

  dealerPriceValue(productCode: string): number {
    if (this.dealerPrices) {
      return this.dealerPriceService.getDealerPriceValueByProductCode(this.dealerPrices, productCode);
    }
  }

  getImageRendition(image: any) {
    let size: string;
    size = ShimanoUtilMethod.imageRendition(177);
    return ShimanoUtilMethod.newImageFormat(image, size);
  }

  goToPdp(product: Product, event: any, position: number) {
    if (!this.hasPointerEventsNone(event.target)) {
      this.routingService.go({
        cxRoute: 'product',
        params: product,
      });
      this.gtmProductClick(product, position);
    }
  }

  hasPointerEventsNone(element: Element): boolean {
    const closestElement = element.closest(this.noPointerEventsOnElement);
    return closestElement && closestElement.classList.contains(POINTER_EVENTS_NONE_CLASS);
  }

  dealerPriceData(productCode: string): DealerPrice {
    if (this.dealerPrices) {
      return this.dealerPriceService.getDealerPriceData(this.dealerPrices, productCode);
    }
  }

  erpPromoValue(productCode: string): string {
    if (this.dealerPrices) {
      return this.dealerPriceService.getErpPromoByProductCode(this.dealerPrices, productCode);
    }
  }

  outletProductNameWidth(code: string, breakpointChanged = false): number {
    const outletProdNameElement = document.getElementsByClassName(
      ['outlet', 'prod-name', code].join(' ')
    )[0] as HTMLElement;
    if (outletProdNameElement && (!this.promoMaxWidth || breakpointChanged))
      this.promoMaxWidth = outletProdNameElement.offsetWidth;
    return this.promoMaxWidth;
  }

  promoTextMaxWidth$(code: string): Observable<number> {
    return this.breakpointService.breakpoint$.pipe(
      startWith(null),
      pairwise(),
      map(([prevBreakpoint, currentBreakpoint]) =>
        !prevBreakpoint
          ? this.outletProductNameWidth(code, false)
          : prevBreakpoint !== currentBreakpoint
          ? this.outletProductNameWidth(code, true)
          : null
      )
    );
  }

  gtmProductCarouselNormalImpressions(products: any[], count: number) {
    const impressionsList: any[] = [];
    products?.forEach((product, index) => {
      if (product) {
        impressionsList.push({
          item_id: product.code,
          item_name: product.name,
          affiliation: this.affiliation,
          index: index,
          item_brand: product?.brand?.name,
          price: this.dealerPriceValue(product.code),
          item_list_name: this.location,
        });
      }
    });
    const gtmTag = {
      event: GoogleTagManagerEvent.PRODUCT_IMPRESSION,
      ecommerce: {
        currency: this.storageService.getSessionStorageItem('currency'),
        items: impressionsList,
      },
    };
    if (count === gtmTag.ecommerce.items.length) {
      this.gtmService.pushTag({
        ecommerce: null,
        search_term: '',
        results_page: '',
        affiliation: '',
        content_type: '',
        content_id: '',
        content_name: '',
      });
      this.gtmService.pushTag(gtmTag);
    }
  }

  gtmProductClick(product: Product, position: number) {
    const gtmTag = {
      event: GoogleTagManagerEvent.PRODUCT_CLICK,
      ecommerce: '',
      content_type: 'product',
      content_id: product.code,
      content_name: product.name,
    };

    this.gtmService.pushTag({
      ecommerce: null,
      search_term: '',
      results_page: '',
      affiliation: '',
      content_type: '',
      content_id: '',
      content_name: '',
    });
    this.gtmService.pushTag(gtmTag);
  }

  ngOnDestroy() {
    if (this.srpSubscription$) {
      this.srpSubscription$.unsubscribe();
    }
    if (this.dealerPriceCheckerSubscription$) {
      this.dealerPriceCheckerSubscription$.unsubscribe();
    }
  }
}
